import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// EVERY field the broker form can edit MUST be listed here.
//
// This mapper is the only path from the API document to the UI object, so a
// field missing from it is invisible no matter how correctly it is stored.
// Detail-view fields (address, postalCode, iban, bankName) were dropped once
// already, which made the ContactInfo fix from commit 0e1b6da appear to "still
// not work" in Test Run 7-22 even though the render code was correct.
//
// It happened again with registrationAuthority / registrationNumber, and that
// time it was worse than invisible. The edit modal seeds its inputs from this
// object, so the two fields reopened blank and saving PATCHed empty strings
// over values that HAD been stored — the form quietly destroyed its own data
// on the second save. That is why every ARGOS broker read as having no
// registration: it was never a data-entry gap.
//
// brokerFieldsSurviveRoundTrip.test.mjs now fails if a key the form can edit
// is absent here.
const toBroker = (b) => ({
  id:         b._id || b.id,
  name:       b.name,
  contact:    b.contact,
  email:      b.email,
  phone:      b.phone,
  status:     b.status || 'active',
  // No `|| '—'` here. This object seeds the edit modal as well as the display,
  // so a display placeholder baked in at the mapper becomes a value the form
  // submits: open Edit on a sponsor with no plan, save, and the record stored
  // a literal em dash. Both display sites (Table, PortfolioSummary) already
  // fall back to '—' themselves.
  commPlan:   b.commPlan,
  // Computed by listBrokers from the Policy collection. `?? null` rather than
  // `|| 0`: the old `policies` field was a stored counter nothing maintained,
  // and reading it rendered a confident 0 for every Sponsor. A Sponsor with
  // genuinely no policies gets a real 0 from the aggregation; only a payload
  // that omitted the field entirely renders an em dash.
  policyCount: b.policyCount ?? null,
  // Computed by GET /brokers/:id from the Policy collection. `?? null` rather
  // than `|| 0`: on the LIST payload the field is absent because only the
  // detail endpoint computes it, and absent must not render as a confident
  // zero — that is the exact defect this replaced.
  activePolicies: b.activePolicies ?? null,
  address:    b.address,
  postalCode: b.postalCode,
  city:       b.city,
  canton:     b.canton,
  bankName:   b.bankName,
  iban:       b.iban,
  // Regulatory registration — read by ContactInfo and seeded into the edit
  // modal. See the note above: omitting these is what made them unsaveable.
  registrationAuthority: b.registrationAuthority,
  registrationNumber:    b.registrationNumber,
  sinceDate:  b.sinceDate,
  lossRatio:  b.lossRatio || 0,
  commissionHistory: b.commissionHistory || [],
});

const useBrokersFunctions = () => {
  useLocale();
  const [brokers, setBrokers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [associatedClients, setAssociatedClients] = useState([]);
  const [page, setPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBroker, setNewBroker] = useState({ name: '', contact: '', email: '', phone: '', address: '', postalCode: '', city: '', canton: '', commPlan: '', registrationAuthority: '', registrationNumber: '' });
  const [submitting, setSubmitting] = useState(false);
  const PAGE_SIZE = 20;

  const fetchBrokers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const resp = await axios.get(`${BASE_URL}/brokers`, { params, withCredentials: true });
      setBrokers((resp.data?.data || []).map(toBroker));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load brokers');
      setBrokers([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchBrokers();
  }, [fetchBrokers]);

  // ─── The one way to open a Sponsor's detail ───────────────
  //
  // Both entry points — clicking a row and deep-linking with ?open=<id> —
  // come through here, and here is the only place that decides what the
  // detail card is built from.
  //
  // They used to differ. The deep link fetched GET /brokers/:id; a row click
  // passed the LIST row straight into setSelectedBroker. That was invisible
  // until the list and the detail stopped carrying the same fields:
  // `activePolicies` is computed per-broker by the detail endpoint and is
  // deliberately absent from the list projection, so a row click rendered an
  // em dash where the deep link rendered 2. Two doors into one screen, and
  // only one of them loaded the screen's data.
  //
  // The fix is not to widen the list projection — that would make every list
  // request pay for a per-row count nothing on the list displays, and would
  // leave the two paths free to diverge again on the next field. It is to
  // have one loader.
  //
  // `detailReqRef` keeps the latest request authoritative: clicking one row
  // and then another cannot let a slow first response overwrite the second.
  // It replaces the per-effect `cancelled` flag and covers both callers.
  const detailReqRef = useRef(0);
  const openBrokerDetail = useCallback(async (brokerId) => {
    if (!brokerId) return;
    const seq = ++detailReqRef.current;
    setDetailLoading(true);
    try {
      const resp = await axios.get(`${BASE_URL}/brokers/${brokerId}`, { withCredentials: true });
      const raw = resp.data?.data?.data || resp.data?.data || null;
      if (seq === detailReqRef.current && raw) setSelectedBroker(toBroker(raw));
    } catch {
      /* broker not found or not permitted — leave the list as it is */
    } finally {
      if (seq === detailReqRef.current) setDetailLoading(false);
    }
  }, []);

  // Closing the card is not a load, so it stays a plain state write. Anything
  // that OPENS the card must go through openBrokerDetail.
  const closeBrokerDetail = useCallback(() => {
    detailReqRef.current += 1; // discard any in-flight response
    setDetailLoading(false);
    setSelectedBroker(null);
  }, []);

  // D-23 retest: deep-link from PolicyDetail broker → /brokers?open=<id>.
  // Clears the query param after opening so a hard-refresh doesn't re-open.
  // Same pattern as OffersScreen ?open handling (D-11).
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    if (!openId) return;
    openBrokerDetail(openId).finally(() => history.replace(location.pathname));
  }, [location.pathname, location.search, history, openBrokerDetail]);

  // Fetch associated clients (policies) when a broker is selected
  useEffect(() => {
    if (!selectedBroker) {
      setAssociatedClients([]);
      return;
    }
    const fetchClients = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/policies`, {
          params: { limit: 100, search: selectedBroker.name },
          withCredentials: true,
        });
        const policies = resp.data?.data || [];
        // Group by policyholder
        const clientMap = {};
        policies.forEach(p => {
          const name = p.policyholder?.name || p.policyholder?.company || 'Unknown';
          if (!clientMap[name]) clientMap[name] = { name, policies: 0, totalPremium: 0, lastDate: null };
          clientMap[name].policies += 1;
          clientMap[name].totalPremium += p.annualPremium || 0;
          const d = p.updatedAt || p.createdAt;
          if (d && (!clientMap[name].lastDate || new Date(d) > new Date(clientMap[name].lastDate))) {
            clientMap[name].lastDate = d;
          }
        });
        setAssociatedClients(Object.values(clientMap));
      } catch {
        setAssociatedClients([]);
      }
    };
    fetchClients();
  }, [selectedBroker]);

  const filtered = (brokers || []).filter(b => {
    const q = search.toLowerCase();
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (q && !b.name?.toLowerCase().includes(q) && !b.contact?.toLowerCase().includes(q) && !b.city?.toLowerCase().includes(q)) return false;
    return true;
  });

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // D-14: broker delete was absent from the SelectedBroker header even
  // though DELETE /brokers/:id exists on the backend (brokers.delete
  // gated to ORG_ADMINS). Adding here so the Header's onDelete prop
  // has a handler to call. Optimistically prunes the row + closes the
  // detail on success; refetches on error to resync.
  const handleDeleteBroker = async () => {
    if (!selectedBroker?.id) return;
    setSubmitting(true);
    try {
      await axios.delete(`${BASE_URL}/brokers/${selectedBroker.id}`, { withCredentials: true });
      setBrokers(prev => (prev || []).filter(b => b.id !== selectedBroker.id));
      closeBrokerDetail();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete broker');
      fetchBrokers();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBroker = async () => {
    if (!newBroker.name.trim()) return;
    setSubmitting(true);
    try {
      if (selectedBroker) {
        const editedId = selectedBroker.id;
        const resp = await axios.patch(`${BASE_URL}/brokers/${editedId}`, newBroker, { withCredentials: true });
        if (resp.data?.data) {
          fetchBrokers();
          // Rehydrate through the one loader rather than from the PATCH
          // response. updateBroker returns the stored document, which carries
          // no computed activePolicies — populating the card from it directly
          // would blank the count again the moment a sponsor is edited, which
          // is the same defect by a different route.
          await openBrokerDetail(editedId);
        }
      } else {
        const resp = await axios.post(`${BASE_URL}/brokers`, newBroker, { withCredentials: true });
        if (resp.data?.data) setBrokers(prev => [...prev, toBroker(resp.data.data)]);
      }
    } catch {
      setBrokers(prev => [...prev, { ...newBroker, id: Date.now(), status: 'active', policyCount: 0, lossRatio: 0 }]);
    } finally {
      setSubmitting(false);
      setShowAddForm(false);
      setNewBroker({ name: '', contact: '', email: '', phone: '', address: '', postalCode: '', city: '', canton: '', commPlan: '', registrationAuthority: '', registrationNumber: '' });
    }
  };

  return {
    brokers,
    setBrokers,
    loading,
    setLoading,
    error,
    setError,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedBroker,
    setSelectedBroker: closeBrokerDetail,
    openBrokerDetail,
    detailLoading,
    associatedClients,
    setAssociatedClients,
    page,
    setPage,
    showAddForm,
    setShowAddForm,
    newBroker,
    setNewBroker,
    submitting,
    setSubmitting,
    PAGE_SIZE,
    fetchBrokers,
    filtered,
    paginated,
    handleAddBroker,
    handleDeleteBroker,
  };
};

export default useBrokersFunctions;
